
import Vue from 'vue';
import { mapState } from 'vuex';

import i18n from '@/i18n';
import api from '@/api';
import { RootActions } from '@/store/types';
import AutocompleteControl, { AutocompletePromise, AutocompleteOption } from '@/components/controls/autocomplete.vue';
import Taxonomy from '@/store/models/taxonomy';

export default Vue.extend({
  name: 'TreatmentSelectionControl',
  inject: {
    $validator: '$validator',
  },
  components: {
    AutocompleteControl,
  },
  props: {
    /**
     * When set, only treatments belonging to this product (from the products tree) are listed,
     * matching the resource create form behavior.
     */
    productId: {
      type: Number,
      default: undefined,
    },
    value: {
      type: Number,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: i18n.t('Treatment'),
    },
    label: {
      type: String,
      default: i18n.t('Treatment'),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: 'treatment',
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalValue: undefined,
      internalLabel: '',
      cancelTokenSource: undefined,
      isLoadingTreatment: false,
    };
  },
  computed: {
    ...mapState('products', {
      productsTree: 'tree',
    }),
    /** Treatments for the selected product (same grouping as `resource.vue`). */
    treatmentsForProduct(): Taxonomy[] {
      if (!this.productId) {
        return [];
      }
      const product = this.productsTree.find((p: { id: number }) => Number(p.id) === Number(this.productId));
      return product?.treatments ?? [];
    },
    autocompleteValue() {
      return {
        label: this.internalLabel,
        value: this.value,
      };
    },
  },
  methods: {
    handleInput(option: AutocompleteOption): void {
      this.internalLabel = option.label;

      let value: number | undefined = Number(option.value);
      if (Number.isNaN(value)) {
        value = undefined;
      }

      if (this.value !== value) {
        this.internalValue = value;
        this.$emit('input', value);
      }
    },
    handleChange(option: AutocompleteOption): void {
      this.internalValue = option.value;
      this.internalLabel = option.label;
      this.$emit('input', Number(option.value));
    },
    loadTreatmentName(): void {
      if (this.value === this.internalValue) {
        return;
      }

      if (!this.value) {
        this.internalLabel = '';

        return;
      }

      if (this.productId) {
        const list = this.treatmentsForProduct;
        if (!list.length) {
          if (!this.value) {
            this.internalLabel = '';
            this.internalValue = undefined;
          }
          return;
        }
        const t = list.find((x) => Number(x.id) === Number(this.value));
        if (t) {
          this.internalLabel = t.title;
          this.internalValue = this.value;
        } else {
          this.internalLabel = '';
          this.internalValue = undefined;
          this.$emit('input', undefined);
        }
        return;
      }

      this.isLoadingTreatment = true;
      api.treatments
        .get(this.value)
        .then((response) => {
          this.internalLabel = response.data.title;
          this.internalValue = this.value;
          this.isLoadingTreatment = false;
        })
        .catch((error) => {
          this.isLoadingTreatment = false;
          this.$store.dispatch(RootActions.ERROR, error);
          this.internalLabel = '';
          this.internalValue = undefined;
          this.$emit('input', undefined);
        });
    },
    searchTreatment(userInput?: string): AutocompletePromise {
      if (this.productId) {
        let list = this.treatmentsForProduct;
        if (userInput && userInput.trim()) {
          const q = userInput.toLowerCase();
          list = list.filter((t) => (t.title || '').toLowerCase().indexOf(q) !== -1);
        }
        return Promise.resolve({
          results: list.map((item) => ({
            label: item.title ?? `#${item.id}`,
            value: item.id,
          })),
        });
      }

      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel();
        this.cancelTokenSource = undefined;
      }

      this.cancelTokenSource = api.getCancelTokenSource();

      const params = userInput ? { search: userInput } : { page: 1 };

      return api.treatments
        .list(params, { cancelToken: this.cancelTokenSource.token })
        .then((response) => {
          return {
            results: response.data.data.map((item) => ({
              label: item.title ?? `#${item.id}`,
              value: item.id,
            })),
          };
        });
    },
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.loadTreatmentName();
      },
    },
    productId: {
      handler(newId: number | undefined) {
        if (!newId) {
          if (this.value != null && typeof this.value !== 'undefined') {
            this.internalLabel = '';
            this.internalValue = undefined;
            this.$emit('input', undefined);
          }
          return;
        }
        const allowedIds = this.treatmentsForProduct.map((t) => Number(t.id));
        if (
          this.treatmentsForProduct.length &&
          this.value != null &&
          !allowedIds.includes(Number(this.value))
        ) {
          this.internalLabel = '';
          this.internalValue = undefined;
          this.$emit('input', undefined);
        } else {
          this.loadTreatmentName();
        }
      },
    },
    treatmentsForProduct: {
      handler() {
        if (!this.productId || this.value == null) {
          return;
        }
        const list = this.treatmentsForProduct;
        if (!list.length) {
          return;
        }
        const t = list.find((x) => Number(x.id) === Number(this.value));
        if (t) {
          this.internalLabel = t.title;
          this.internalValue = this.value;
        } else {
          this.internalLabel = '';
          this.internalValue = undefined;
          this.$emit('input', undefined);
        }
      },
      deep: true,
    },
  },
});
